import React, { useCallback, useEffect, useState } from 'react';
import InputNumberFormat from 'react-number-format';

import { Plus as PlusIcon } from '@styled-icons/bootstrap/Plus';
import { Check as CheckIcon } from '@styled-icons/boxicons-regular/Check';
import { Save as SaveIcon } from '@styled-icons/boxicons-solid/Save';
import { TrashAlt as TrashAltIcon } from '@styled-icons/boxicons-solid/TrashAlt';
import { Close as CloseIcon } from '@styled-icons/evaicons-solid/Close';
import { Refresh as RefreshIcon } from '@styled-icons/material-twotone/Refresh';
import { api } from 'services/api';
import { toast } from 'shared/toast';

import {
  Box,
  Button,
  Icon,
  Text,
  IconButton,
  Flex,
  HStack,
  Input as ChakraInput,
  InputGroup as ChakraInputGroup,
  InputRightAddon,
  Stack,
  useBreakpointValue,
} from '@chakra-ui/react';

import AppTable from 'components/AppTable';

import useModal from 'hooks/useModal';

import { InputGroup } from 'types/input';

interface PriceByGroupProps {
  composition: {
    prices?: Array<{
      composition_id?: number;
      id?: number;
      price_specificity?: Array<{
        total: number;
        description: string;
        category: string;
        referenceId: string;
      }> | null;
    }>;
  };
  isLoading: boolean;
  onUpdate?: () => void;
  isFromModal?: boolean;
  onSpecificitiesChange?: (
    specificities: Array<{
      referenceId: string;
      description: string;
      category: string;
      total: number;
    }>,
  ) => void;
  onSubmitSpecificities?: (
    priceSpecificity: Array<{
      referenceId: string;
      description: string;
      category: string;
      total: number;
    }>,
  ) => Promise<void> | void;
  submitButtonLabel?: string;
  showRefreshButton?: boolean;
}

type SpecificityRow = {
  id: string | number;
  description: string;
  category: string;
  referenceId: string;
  total: number;
};

const formatCurrency = (value?: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value ?? 0);
};

const PriceByGroup: React.FC<PriceByGroupProps> = ({
  composition,
  isLoading,
  onUpdate,
  isFromModal = false,
  onSpecificitiesChange,
  onSubmitSpecificities,
  submitButtonLabel = 'Salvar',
  showRefreshButton = true,
}) => {
  const { openModal } = useModal();
  const [specificities, setSpecificities] = useState<SpecificityRow[]>([]);
  const [editingTotal, setEditingTotal] = useState<{
    id: SpecificityRow['id'];
    value: number;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const isCompactActions = useBreakpointValue({ base: true, md: false });
  const actionButtonSize = isCompactActions ? 'sm' : 'md';
  const mapSpecificityRowsToPayload = useCallback(
    (rows: SpecificityRow[]) =>
      rows.map((row) => ({
        referenceId: row.referenceId,
        description: row.description,
        category: row.category,
        total: row.total || 0,
      })),
    [],
  );

  useEffect(() => {
    const [firstPrice] = composition?.prices || [];

    const items = firstPrice?.price_specificity || [];

    // In modal creation flow there is no persisted composition price yet.
    // Avoid wiping user-added rows if parent re-renders with the same empty prices.
    if (isFromModal && !items.length) {
      return;
    }

    const mappedSpecificities = items.map((item, index) => {
      const fallbackId = `existing-${index}`;
      const referenceId = item.referenceId || fallbackId;

      return {
        id: referenceId,
        description: item.description || '-',
        category: item.category || '-',
        referenceId,
        total: item.total || 0,
      };
    });

    setSpecificities(mappedSpecificities);
    setEditingTotal(null);
  }, [composition.prices, isFromModal]);

  const handleAddGroups = useCallback(
    (groups: InputGroup[]) => {
      setSpecificities((prevSpecificities) => {
        const existingReferences = new Set(
          prevSpecificities.map((item) => item.referenceId),
        );

        const newSpecificities = groups
          .filter((group) => !existingReferences.has(group.key))
          .map((group) => ({
            id: group.key,
            description: group.description || '-',
            category: group.type || '-',
            referenceId: group.key,
            total: 0,
          }));

        if (!newSpecificities.length) {
          return prevSpecificities;
        }

        const nextSpecificities = [...prevSpecificities, ...newSpecificities];
        onSpecificitiesChange?.(mapSpecificityRowsToPayload(nextSpecificities));
        return nextSpecificities;
      });
    },
    [mapSpecificityRowsToPayload, onSpecificitiesChange],
  );

  const handleRemoveSpecificity = useCallback(
    (specificityId: SpecificityRow['id']) => {
      setSpecificities((prevSpecificities) => {
        const nextSpecificities = prevSpecificities.filter(
          (item) => item.id !== specificityId,
        );
        onSpecificitiesChange?.(mapSpecificityRowsToPayload(nextSpecificities));
        return nextSpecificities;
      });
      setEditingTotal((currentEdit) =>
        currentEdit?.id === specificityId ? null : currentEdit,
      );
    },
    [mapSpecificityRowsToPayload, onSpecificitiesChange],
  );

  const handleStartEditingTotal = useCallback((row: SpecificityRow) => {
    setEditingTotal({ id: row.id, value: row.total ?? 0 });
  }, []);

  const handleCancelEditingTotal = useCallback(() => {
    setEditingTotal(null);
  }, []);

  const handleConfirmEditingTotal = useCallback(
    (row: SpecificityRow) => {
      if (!editingTotal || editingTotal.id !== row.id) return;

      setSpecificities((prevSpecificities) => {
        const nextSpecificities = prevSpecificities.map((item) =>
          item.id === row.id ? { ...item, total: editingTotal.value } : item,
        );
        onSpecificitiesChange?.(mapSpecificityRowsToPayload(nextSpecificities));
        return nextSpecificities;
      });

      setEditingTotal(null);
    },
    [editingTotal, mapSpecificityRowsToPayload, onSpecificitiesChange],
  );

  const handleSaveSpecificities = useCallback(async () => {
    const payload = specificities.map((item) => {
      const value =
        editingTotal && editingTotal.id === item.id
          ? editingTotal.value
          : item.total;

      return {
        referenceId: item.referenceId,
        description: item.description,
        category: item.category,
        total: value ?? 0,
      };
    });

    const hasZeroTotal = payload.some(
      (specificity) => !specificity.total || specificity.total === 0,
    );

    if (hasZeroTotal) {
      toast({
        description:
          'Há grupos com valor zerado. Informe um preço válido antes de salvar.',
        status: 'warning',
      });
      return;
    }

    setSaving(true);

    try {
      if (onSubmitSpecificities) {
        await onSubmitSpecificities(payload);
      } else {
        const [firstPrice] = composition?.prices || [];
        const compositionId = firstPrice?.composition_id;
        const priceId = firstPrice?.id;

        if (!compositionId || !priceId) {
          toast({
            description:
              'Não foi possível identificar o preço deste Serviço a Preço Informado para salvar as especificidades.',
            status: 'error',
          });
          setSaving(false);
          return;
        }

        await api.put(`/composition/${compositionId}/price/${priceId}`, {
          price_specificity: payload,
        });
      }

      setSpecificities((prevSpecificities) =>
        prevSpecificities.map((item) => {
          if (editingTotal && editingTotal.id === item.id) {
            return { ...item, total: editingTotal.value };
          }

          return item;
        }),
      );
      setEditingTotal(null);

      if (!isFromModal) {
        toast({
          description: onSubmitSpecificities
            ? 'Preço por grupo salvo com sucesso.'
            : 'Preço por grupo atualizado com sucesso.',
          status: 'success',
        });
      }

      if (onUpdate) {
        onUpdate();
      }
    } catch (err) {
      const description =
        err?.response?.data?.message ||
        'Não foi possível salvar o preço por grupo. Tente novamente.';

      toast({
        description,
        status: 'error',
      });
    } finally {
      setSaving(false);
    }
  }, [
    composition?.prices,
    editingTotal,
    isFromModal,
    onSubmitSpecificities,
    onUpdate,
    specificities,
  ]);

  return (
    <Flex
      w="100%"
      alignItems="flex-start"
      justifyContent="center"
      flexDirection="column"
      sx={{
        '> *': {
          marginY: 3,
        },
      }}
    >
      <Flex
        w="100%"
        direction={{ base: 'column', lg: 'row' }}
        justify="space-between"
        align={{ base: 'stretch', lg: 'flex-end' }}
        gap={4}
        mt={10}
        flexWrap="wrap"
      >
        <Stack
          direction={{ base: 'column', sm: 'row' }}
          spacing={3}
          flex="1"
          align={{ base: 'stretch', sm: 'center' }}
        >
          <Button
            size={actionButtonSize}
            colorScheme="green"
            w={{ base: '100%', sm: 'auto' }}
            leftIcon={<Icon as={PlusIcon} />}
            onClick={() => {
              openModal('compositionGroupsLookup', {
                mode: 'lookup',
                data: {
                  resourceType: 'input',
                  multiSelect: true,
                },
                onConfirm: (groupsWithParent) => {
                  if (Array.isArray(groupsWithParent)) {
                    const groupsToAdd = groupsWithParent.map(
                      (selection) => selection.selectedGroup,
                    );

                    handleAddGroups(groupsToAdd);
                  } else {
                    handleAddGroups([groupsWithParent.selectedGroup]);
                  }
                },
              });
            }}
          >
            Adicionar grupos de insumo
          </Button>

          {showRefreshButton && (
            <IconButton
              size={actionButtonSize}
              colorScheme="blue"
              title="Atualizar"
              aria-label="Atualizar"
              icon={<Icon as={RefreshIcon} w={5} h={5} />}
              isLoading={isLoading}
              isDisabled={isLoading}
              onClick={() => {
                onUpdate?.();
              }}
              w={{ base: '100%', sm: 'auto' }}
            />
          )}
        </Stack>

        <Stack
          direction={{ base: 'column', sm: 'row' }}
          spacing={3}
          align={{ base: 'stretch', sm: 'center' }}
          justify="flex-end"
        >
          <Button
            size={actionButtonSize}
            colorScheme="green"
            w={{ base: '100%', sm: 'auto' }}
            leftIcon={<Icon as={CheckIcon} />}
            onClick={handleSaveSpecificities}
            isLoading={saving}
            isDisabled={saving || isLoading || specificities.length === 0}
          >
            {submitButtonLabel}
          </Button>
        </Stack>
      </Flex>
      <Box
        p={isFromModal ? undefined : 4}
        width="100%"
        background="white"
        border={isFromModal ? 'none' : '1px solid'}
        borderColor={isFromModal ? undefined : 'gray.200'}
        borderRadius={isFromModal ? undefined : '8px'}
      >
        <AppTable
          loading={isLoading}
          data={specificities}
          cols={[
            { field: 'key', description: 'Código', sortable: false },
            { field: 'description', description: 'Grupo', sortable: false },
            {
              field: 'total',
              description: 'Preço',
              isNumeric: true,
              sortable: false,
            },
            {
              field: 'actions',
              description: 'Ações',
              sortable: false,
              isCentered: true,
            },
          ]}
          mapping={(item) => ({
            id: item.id,
            key: <Text fontWeight="bold">{item.referenceId}</Text>,
            description: (
              <Text whiteSpace="pre-wrap" maxW={{ base: '100%', md: '480px' }}>
                {item.description}
              </Text>
            ),
            total:
              editingTotal?.id === item.id ? (
                <ChakraInputGroup>
                  <InputNumberFormat
                    type="tel"
                    customInput={ChakraInput}
                    displayType="input"
                    thousandSeparator="."
                    decimalSeparator=","
                    decimalScale={2}
                    fixedDecimalScale
                    allowNegative={false}
                    value={editingTotal.value}
                    autoFocus
                    onFocus={(e) => e.target.select()}
                    onValueChange={(values) => {
                      setEditingTotal((currentEdit) => {
                        if (!currentEdit || currentEdit.id !== item.id) {
                          return currentEdit;
                        }

                        return {
                          ...currentEdit,
                          value: values.floatValue ?? 0,
                        };
                      });
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        handleConfirmEditingTotal(item);
                      }

                      if (event.key === 'Escape') {
                        handleCancelEditingTotal();
                      }
                    }}
                    style={{
                      textAlign: 'right',
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                    }}
                  />

                  <InputRightAddon px={0}>
                    <IconButton
                      height="100%"
                      colorScheme="blue"
                      aria-label="Salvar valor"
                      title="Salvar valor"
                      borderRadius="0"
                      onClick={() => handleConfirmEditingTotal(item)}
                      width="34px"
                    >
                      <Icon w={4} h={4} as={SaveIcon} />
                    </IconButton>

                    <IconButton
                      height="100%"
                      colorScheme="red"
                      aria-label="Cancelar edição"
                      title="Cancelar edição"
                      borderTopLeftRadius="0"
                      borderBottomLeftRadius="0"
                      onClick={handleCancelEditingTotal}
                      width="34px"
                    >
                      <Icon w={4} h={4} as={CloseIcon} />
                    </IconButton>
                  </InputRightAddon>
                </ChakraInputGroup>
              ) : (
                <Text
                  cursor="pointer"
                  fontWeight="medium"
                  color="blue.500"
                  _hover={{ color: 'blue.600', textDecor: 'underline' }}
                  onClick={() => handleStartEditingTotal(item)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleStartEditingTotal(item);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                >
                  {formatCurrency(item.total)}
                </Text>
              ),
            actions: (
              <HStack justifyContent="center">
                <IconButton
                  aria-label="Remover grupo de insumo"
                  onClick={() => handleRemoveSpecificity(item.id)}
                  colorScheme="red"
                >
                  <Icon as={TrashAltIcon} />
                </IconButton>
              </HStack>
            ),
          })}
          noDataMessage="Nenhum grupo de insumo cadastrado para este preço."
        />
      </Box>
    </Flex>
  );
};

export default PriceByGroup;
